import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import BlockContainer from './BlockContainer';
import CardGrid, { getCardColumns } from './CardGrid';
import DMResultCard, { getDMCardImageUrl } from './DMResultCard';
import DMObjectModal, { DMObjectPreview } from './DMObjectModal';
import { LightboxItem, LightboxWrapper } from './LightboxModal';
import Pagination from './Pagination';
import useBlockPaging from '../hooks/useBlockPaging';
import { useDMOwnerNames } from '../hooks/useDMSearch';
import { getReadablePalette } from '../common/Color';
import { preloadImages } from '../common/Media';
import { DMBaseSearch, DMItem, buildDMBaseSearch, getDMImageUrl, getDMSortQuery, isDMIncludeLink, opensAsDMPage, resolveDMSelection, searchDM, sortDMDocsByOrder, toDMItem } from '../services/DigitaltMuseum';
import { AppContext } from '../contexts/AppContext';

interface BlockDMProps {
  /**
   * The article's DM block: `dmBlockType` "search" (a dynamic search in `dmSearch`) or "items" (hand-picked
   * Kulturio media objects in `dmItems`, shown in the CMS order), and `includeLink`
   */
  block: any
}

/** Objects per page without resultCount, in a search and in a hand-picked selection */
const DEFAULT_COUNT = 8;
const SELECTION_PAGE_SIZE = 10;

/** Pages go no further than this many hits, since broad searches (e.g. all of DM) have millions and deep paging is slow */
const MAX_HITS = 1000;

/** Same cards as the DM view, four across in landscape, colored like the other article blocks (text boxes, Minne) */
const Grid = styled(CardGrid)`
  & > li {
    background-color: ${p => p.theme.blockColor};
  }
`;

/** Load the cards' images before showing them, so a new page changes in one go */
const preloadCardImages = (items: DMItem[], wide: boolean) =>
  preloadImages(items.filter(item => item.mediaIdentifier).map(item => getDMCardImageUrl(item.mediaIdentifier!, wide)));

/**
 * DigitaltMuseum objects in an article: a dynamic search (paged in DM) or a hand-picked selection (fetched once
 * and paged here), with the cards from the DM view. A single object gets a wide card. With includeLink a tap
 * opens the DM object popup, otherwise the image opens in the lightbox
 * @returns {JSX.Element} Component template
 */
const BlockDM: FC<BlockDMProps> = ({ block }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { previewIsActive } = useContext(AppContext);
  // The article this block is in, so a folder or article opened from it can go back to it
  const { deviceId, documentId } = useParams<{ deviceId?: string, documentId?: string }>();
  const isSelection = block?.dmBlockType === "items";
  const search = block?.dmSearch;
  const selection: any[] = useMemo(() => isSelection ? block?.dmItems || [] : [], [isSelection, block?.dmItems]);
  // Searches have includeLink in dmSearch, selections on the block
  const includeLinkSetting = isSelection ? block?.includeLink : search?.includeLink ?? block?.includeLink;
  const includeLink = isDMIncludeLink(includeLinkSetting);

  const [items, setItems] = useState<DMItem[]>([]);
  const [numFound, setNumFound] = useState<number>(0);
  const [selected, setSelected] = useState<DMObjectPreview | null>(null);
  // A hand-picked selection, all objects in the CMS order (fetched once)
  const [selectionItems, setSelectionItems] = useState<DMItem[] | null>(null);
  const palette = useMemo(() => getReadablePalette(theme?.backgroundColor), [theme?.backgroundColor]);
  const ownerNames = useDMOwnerNames(items, []);

  // resultCount is the number per page: in dmSearch for a search, on the block for a selection
  const pageSize = Number(isSelection ? block?.resultCount : search?.resultCount) || (isSelection ? SELECTION_PAGE_SIZE : DEFAULT_COUNT);
  const sourceKey = JSON.stringify(isSelection ? selection : search || {});
  const { currentPage, setCurrentPage, listRef, listStyle, idleResetCount } = useBlockPaging(sourceKey);
  // A new seed per search (and idle reset), so random order stays the same across pages
  const seed = useMemo(() => Date.now(), [sourceKey, idleResetCount]); // eslint-disable-line react-hooks/exhaustive-deps
  // Only objects with images in a search, as before
  const searchBase: DMBaseSearch | null = useMemo(() => !isSelection && search ? buildDMBaseSearch({ ...search, onlyWithImages: true }, null, search.sort) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sourceKey]);

  // Hand-picked selection: find the objects once, in the CMS order (also those without images)
  useEffect(() => {
    if (!isSelection) { return; }
    let isActive = true;
    setSelectionItems(null);
    const mediaIds = selection.map(item => item?.mediaId).filter(Boolean);
    if (mediaIds.length === 0) { return; }
    resolveDMSelection(mediaIds).then(async context => {
      const result = await searchDM(buildDMBaseSearch({}, context), {}, { rows: context.order?.length });
      isActive && setSelectionItems(sortDMDocsByOrder(result?.response?.docs || [], context.order || []).map(toDMItem));
    }).catch(() => console.error("Could not find the hand-picked DM objects"));
    return () => { isActive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey]);

  // The current page: a slice of the selection, or a page from the search
  useEffect(() => {
    let isActive = true;
    const show = async (page: DMItem[], total: number) => {
      await preloadCardImages(page, total === 1);
      if (!isActive) { return; }
      setItems(page);
      setNumFound(total);
    };
    if (isSelection) {
      selectionItems && show(selectionItems.slice((currentPage - 1) * pageSize, currentPage * pageSize), selectionItems.length);
    } else if (searchBase) {
      searchDM(searchBase, {}, { rows: pageSize, start: (currentPage - 1) * pageSize, sort: getDMSortQuery(searchBase.sortKey, searchBase, seed) })
        .then(result => show((result?.response?.docs || []).map(toDMItem), Math.min(result?.response?.numFound || 0, MAX_HITS)))
        .catch(() => console.error("Could not fetch DM search for article block"));
    }
    return () => { isActive = false; };
  }, [isSelection, selectionItems, searchBase, currentPage, pageSize, seed]);

  // Close the popup when idle mode resets the kiosk
  useEffect(() => { setSelected(null); }, [idleResetCount]);

  /**
   * Open an object: folders, exhibitions, DM articles and virtual experiences (and their parts) open as a page in the DM view
   * (back returns to this article), other objects in the popup. In preview, which can not navigate, all use the popup
   */
  const openObject = useCallback((item: DMItem, owner?: string) => {
    if (opensAsDMPage(item) && !previewIsActive && deviceId && documentId) {
      navigate(`/device/${deviceId}/dm/${documentId}/${item.uniqueId}`);
    } else {
      setSelected({ ...item, owner });
    }
  }, [previewIsActive, deviceId, documentId, navigate]);

  if (items.length === 0) { return null; }

  const ownerName = (item: DMItem) => item.ownerCode ? ownerNames[item.ownerCode] : undefined;
  // A single object fills the width of the block
  const wide = numFound === 1;

  return (
    <BlockContainer>
      <LightboxWrapper>
        <Grid ref={listRef} style={listStyle} {...getCardColumns(numFound, 4, 3)}>
          {items.map(item => {
            const card = <DMResultCard key={item.uniqueId} item={item} ownerName={ownerName(item)} palette={palette} wide={wide}
              onClick={() => includeLink && openObject(item, ownerName(item))}/>;
            if (includeLink || !item.mediaIdentifier) { return card; }
            const media = {
              url: getDMImageUrl(item.mediaIdentifier),
              media_type: "dm",
              title: item.title,
              credits: ownerName(item) ? [{ credit_type_id: 6, name: ownerName(item) }] : []
            };
            return <LightboxItem key={item.uniqueId} media={media}>{card}</LightboxItem>;
          })}
        </Grid>
      </LightboxWrapper>
      <Pagination currentPage={currentPage} itemCount={numFound} pageSize={pageSize} setCurrentPage={setCurrentPage}/>

      {/* In body, so the popup covers the whole screen whatever transforms the article has */}
      {selected && createPortal(
        <DMObjectModal uniqueId={selected.uniqueId} preview={selected} backgroundColor={theme?.backgroundColor} onClose={() => setSelected(null)}/>,
        document.body
      )}
    </BlockContainer>
  );
}

export default BlockDM;
